import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import {
  ChevronLeft, ChevronRight, Monitor, Smartphone, Loader2, Save,
  KeyRound, MonitorPlay, Globe, Check, RotateCw,
} from 'lucide-react'

import { Card, CardContent } from '@meno/ui/components/ui/card'
import { Button } from '@meno/ui/components/ui/button'
import { WizardNavButton } from '@meno/ui/components/shared/WizardNavButton'
import { Badge } from '@meno/ui/components/ui/badge'
import { Input } from '@meno/ui/components/ui/input'
import { Label } from '@meno/ui/components/ui/label'
import { OtpInput } from '@meno/ui/components/ui/OtpInput'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@meno/ui/components/ui/select'
import { cn } from '@meno/ui/lib/utils'
import {
  getScreens, updateScreen, getBranches, pairScreen, validatePairingCode,
  getAppSubscription,
} from '@meno/ui/lib/api'
import { usePageHeader } from '@meno/ui/components/layout/PageHeader'
import { usePermissions } from '@meno/ui/hooks/usePermissions'
import LicenseRequestDialog from '@/components/LicenseRequestDialog'
import { WEB_PLAYER_URL, WEB_PLAYER_HOST } from '@/lib/webPlayer'

type Branch = { id: number; name: string }
type PlayerType = 'device' | 'web'
type DeviceOS   = 'android' | 'android-tv'

// Opening the browser player in a NEW tab lets the operator start the player
// (which shows the pairing code) without losing this wizard tab. The origin is
// environment-resolved (lib/webPlayer) so a staging wizard sends the operator
// to the staging player — a production pairing code is useless here.
const openWebPlayer = () => window.open(WEB_PLAYER_URL, '_blank', 'noopener,noreferrer')

const EMPTY = {
  name: '', status: 'offline',
  // Two-axis orientation seeded for create. Operator can refine on the
  // detail page after pairing; the wizard only asks for content aspect.
  orientation_type: 'horizontal' as 'horizontal' | 'vertical',
  rotation_degrees: 0 as 0 | 90 | 180 | 270,
  branch_id: '', location: '', ip_address: '',
}

// Seat counts now come from /app-subscriptions/signage (Workstream A). The
// wizard fetches once on mount and gates step-2 → step-3 + the Pair button
// on seats_remaining > 0. Admins grant licenses from the admin app.

const OS_LIST: { key: DeviceOS; label: string }[] = [
  { key: 'android',    label: 'Android'    },
  { key: 'android-tv', label: 'Android TV' },
]

// ── OS visual marks ────────────────────────────────────────────────────────
// Inline SVG placeholders for the OS picker. They read at glance as the
// real platform marks without shipping trademarked assets. Replace with
// licensed SVG/PNG when marketing provides them.
// The Android robot mark, reused by both the phone/tablet and TV variants.
function AndroidRobot({ size }: { size: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-label="Android"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill="#3DDC84"
        d="M17.523 15.341c-.55 0-1-.448-1-.999s.45-.999 1-.999 1 .448 1 .999-.45.999-1 .999m-11.046 0c-.55 0-1-.448-1-.999s.45-.999 1-.999 1 .448 1 .999c0 .551-.45.999-1 .999m11.405-6.02 1.997-3.459a.416.416 0 0 0-.152-.568.416.416 0 0 0-.568.152l-2.022 3.503C15.59 8.244 13.853 7.851 12 7.851s-3.59.393-5.137 1.099L4.841 5.447a.416.416 0 0 0-.568-.152.416.416 0 0 0-.152.568L6.118 9.32C2.689 11.187.343 14.659 0 18.761h24c-.343-4.102-2.689-7.574-6.118-9.44"
      />
    </svg>
  )
}

function OSLogo({ os, size = 48 }: { os: DeviceOS; size?: number }) {
  if (os === 'android-tv') {
    // Same robot as plain Android, with a "TV" wordmark so the two cards read
    // apart at a glance even though they share the platform mark. Used in the
    // small install-guide header; the OS picker uses the full wordmark below.
    return (
      <div className="flex items-center gap-2">
        <AndroidRobot size={size} />
        <span
          className="font-extrabold tracking-tight text-foreground"
          style={{ fontSize: size * 0.5 }}
        >
          TV
        </span>
      </div>
    )
  }
  return <AndroidRobot size={size} />
}

// Official "Android TV" horizontal wordmark, swapped by theme: the dark (RGB)
// lockup on light surfaces, the white (inverse) lockup on dark ones. The `dark:`
// variants fire because the signage app sets `.dark` on the root in dark mode.
// The wordmark already reads "Android TV", so the picker card drops its text
// label when it uses this.
function AndroidTvWordmark() {
  return (
    <>
      <img src="/android-tv-rgb.svg" alt="Android TV" className="block dark:hidden w-40 max-w-[75%] h-auto" />
      <img src="/android-tv-inverse.svg" alt="Android TV" className="hidden dark:block w-40 max-w-[75%] h-auto" />
    </>
  )
}

// Official "Android" horizontal wordmark (robot head + wordmark), theme-swapped
// the same way as the TV lockup above: dark (RGB) on light, white (inverse) on
// dark. The wordmark already reads "Android", so the picker card drops its text
// label when it uses this.
function AndroidWordmark() {
  return (
    <>
      <img src="/android-rgb.svg" alt="Android" className="block dark:hidden w-40 max-w-[75%] h-auto" />
      <img src="/android-inverse.svg" alt="Android" className="hidden dark:block w-40 max-w-[75%] h-auto" />
    </>
  )
}

// ── Branded wizard hero (WeTook mark + title + subtitle) ───────────────────
// Shared by the choose-player landing, the web step, and the OS picker so
// every entry point into the flow reads as WeTook chrome rather than a
// generic form. The mark renders as a solid brand-red "W" (its gray shadow
// path is fully overdrawn by the red one).
function WizardHero({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="text-center mb-10">
      <img src="/frame-logo.avif" alt="WeTook" className="w-12 mx-auto mb-5" />
      <h2 className="text-2xl font-bold tracking-tight mb-2.5">{title}</h2>
      <p className="text-sm text-muted-foreground max-w-md mx-auto">{subtitle}</p>
    </div>
  )
}

// ── Browser-window mock (theme-aware) ──────────────────────────────────────
// A light browser window in light mode, a dark one in dark mode. Uses the zinc
// scale + `dark:` variants instead of bare `bg-white`/`bg-gray-*` because in
// dark mode the app pairs `.dark` with `.admin-dark`, whose `.bg-white`
// escape-hatch re-themes white surfaces to the (dark) card token while the
// gray literals stay light — which is what made the old mock read broken.
function BrowserMock({ t, compact, fill, className }: { t: TFunction; compact?: boolean; fill?: boolean; className?: string }) {
  return (
    // Pinned LTR: this is a picture of a browser window, a universal object —
    // its chrome (window dots, back/forward arrows, address bar) must read the
    // same in Arabic, not mirror with the RTL page (which flipped the arrows to
    // the wrong side + direction).
    <div dir="ltr" className={cn(
      'w-full rounded-xl overflow-hidden bg-zinc-50 dark:bg-zinc-900 shadow-sm border border-zinc-200 dark:border-zinc-700',
      fill && 'flex flex-col h-full',
      className,
    )}>
      <div className="bg-zinc-100 dark:bg-zinc-800 flex items-center gap-1.5 px-3 py-2">
        <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
        <div className="ms-2 inline-flex items-center gap-1.5 bg-zinc-50 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-200 text-[10px] px-2 py-1 rounded-t-md">
          <Globe className="w-3 h-3" />
          {t('screens.wizard.newTab', 'New Tab')}
        </div>
      </div>
      <div className="bg-zinc-50 dark:bg-zinc-900 px-3 py-3 flex items-center gap-2">
        <ChevronLeft className="w-3.5 h-3.5 text-zinc-400 dark:text-zinc-500" />
        <ChevronRight className="w-3.5 h-3.5 text-zinc-400 dark:text-zinc-500" />
        <div className="ms-1 flex-1 bg-zinc-100 dark:bg-zinc-800 rounded-full px-3 py-1.5 text-start">
          <span className={cn('text-primary font-semibold', compact ? 'text-xs' : 'text-base')}>{WEB_PLAYER_HOST}</span>
        </div>
      </div>
      <div className={cn(
        'bg-zinc-50 dark:bg-zinc-900 flex items-center justify-center border-t border-zinc-200 dark:border-zinc-800',
        fill ? 'flex-1' : compact ? 'h-16' : 'h-20',
      )}>
        <img src="/frame-logo.avif" alt="" className={cn('opacity-90', compact ? 'h-7' : 'h-8')} />
      </div>
    </div>
  )
}

// ── Top stepper bar (renders INSIDE the TopBar's h-14 strip via usePageHeader.render) ──
// Progress only — the Back / Next controls live in the content footer below
// (WizardFooter), not in the TopBar.
function StepperBar({ step, t }: { step: 1 | 2 | 3; t: TFunction }) {
  const steps = [
    { n: 1, label: t('screens.wizard.step1', 'Choose Player Type') },
    { n: 2, label: t('screens.wizard.step2', 'Enter Screen Code') },
    { n: 3, label: t('screens.wizard.step3', 'Screen Details') },
  ] as const

  return (
    <div className="w-full h-full flex items-center justify-center px-4">
      <div className="flex items-center gap-2 flex-1 justify-center max-w-3xl">
        {steps.map((s, idx) => {
          const isActive    = step === s.n
          const isCompleted = step > s.n
          return (
            <div key={s.n} className="flex items-center gap-2 flex-1">
              <div className="flex items-center gap-1.5 shrink-0">
                <div
                  className={cn(
                    'w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold transition-colors',
                    isActive      ? 'bg-primary text-primary-foreground'
                    : isCompleted ? 'bg-foreground text-background'
                    : 'bg-muted text-muted-foreground',
                  )}
                >
                  {isCompleted ? <Check className="w-3 h-3" /> : s.n}
                </div>
                <span className={cn(
                  'text-xs font-medium whitespace-nowrap transition-colors hidden md:inline',
                  isActive    ? 'text-foreground'
                  : isCompleted ? 'text-foreground/80'
                  : 'text-muted-foreground',
                )}>{s.label}</span>
              </div>
              {idx < steps.length - 1 && (
                <div className={cn('flex-1 h-px transition-colors', isCompleted ? 'bg-foreground/40' : 'bg-border')} />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Tiny back-only bar for the pre-wizard branch picker (also renders inside
// the TopBar via usePageHeader.render).
function BackOnlyBar({ onBack, t }: { onBack: () => void; t: TFunction }) {
  return (
    <div className="w-full h-full flex items-center px-4">
      <Button variant="secondary" size="sm" onClick={onBack} className="gap-1.5 rounded-full px-3 h-8">
        {/* Always ChevronLeft — a global CSS rule mirrors it under dir="rtl";
            see WizardNavButton.tsx for why swapping here too double-flips it. */}
        <ChevronLeft className="w-3.5 h-3.5" />
        {t('common.back', 'Back')}
      </Button>
    </div>
  )
}

// ── Pre-wizard: pick web vs device branch (back bar lives in TopBar) ──────
function ChooseBranch({
  onPick, t,
}: {
  onPick: (p: PlayerType) => void
  t: TFunction
}) {
  return (
    <div className="max-w-3xl mx-auto pt-10 pb-16 w-full">
      <WizardHero
        title={t('screens.wizard.choosePlayerTitle', 'Choose the player type you want to register on WeTook')}
        subtitle={t('screens.wizard.choosePlayerSubtitle', "Select how you'd like to connect and register your player.")}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Web Browser — branded browser chrome ending in the WeTook mark */}
        <div className="group flex flex-col items-center text-center p-6 rounded-2xl border border-[hsl(var(--border-card,var(--border)))] bg-card hover:border-primary/60 transition-all">
          {/* Shared aspect box so both card images render at the same size */}
          <div className="w-full aspect-[16/10] mb-5">
            <BrowserMock t={t} compact fill className="h-full ring-1 ring-transparent group-hover:ring-primary/15 transition" />
          </div>
          <span className="text-base font-semibold text-foreground">
            {t('screens.wizard.webPlayer', 'Web Browser')}
          </span>
          {/* min-h reserves two lines so this description and the Device card's
              occupy the same height — keeping the badge + CTA rows aligned. */}
          <span className="text-xs text-muted-foreground mt-1 max-w-[16rem] min-h-[2.5rem]">
            {t('screens.wizard.webPlayerDesc', 'Open {{host}} on any browser to register a screen.', { host: WEB_PLAYER_HOST })}
          </span>
          {/* "For …" pill — neutral Badge (no red-on-red / red text) */}
          <Badge variant="secondary" className="mt-3 gap-1.5 rounded-full px-3 py-1 normal-case tracking-normal font-medium text-[11px]">
            <Globe className="w-3 h-3" />
            {t('screens.wizard.forBrowsers', 'For browsers')}
          </Badge>
          {/* Selecting the web player only advances the wizard — it must NOT
              auto-open the player. The next step carries an explicit
              "Open <player host>" button the operator clicks when ready. */}
          <Button
            onClick={() => onPick('web')}
            className="mt-5 w-full h-11 rounded-xl gap-2"
          >
            <Globe className="w-4 h-4" />
            {t('screens.wizard.chooseWebPlayer', 'Use Web Player')}
          </Button>
        </div>

        {/* Device Player — brand-black display carrying the WeTook mark */}
        <div className="group flex flex-col items-center text-center p-6 rounded-2xl border border-[hsl(var(--border-card,var(--border)))] bg-card hover:border-primary/60 transition-all">
          <div className="w-full aspect-[16/10] mb-5 rounded-xl bg-black shadow-sm border border-white/10 overflow-hidden flex items-center justify-center ring-1 ring-transparent group-hover:ring-primary/15 transition">
            {/* Screens product shot (shared with the marketing nav). The image
                is black-backed so it sits seamlessly in the black card. */}
            <img src="/screen.png" alt="" className="w-full h-full object-contain" />
          </div>
          <span className="text-base font-semibold text-foreground">
            {t('screens.wizard.devicePlayer', 'Device Player')}
          </span>
          <span className="text-xs text-muted-foreground mt-1 max-w-[16rem] min-h-[2.5rem]">
            {t('screens.wizard.devicePlayerDesc', 'Use the WeTook native app on Android or Android TV.')}
          </span>
          {/* "For …" pill — neutral Badge (no red-on-red / red text) */}
          <Badge variant="secondary" className="mt-3 gap-1.5 rounded-full px-3 py-1 normal-case tracking-normal font-medium text-[11px]">
            <MonitorPlay className="w-3 h-3" />
            {t('screens.wizard.forDevices', 'For Android & TV')}
          </Badge>
          {/* Explicit action — solid brand CTA (white on brand, not red text) */}
          <Button onClick={() => onPick('device')} className="mt-5 w-full h-11 rounded-xl">
            {t('screens.wizard.openDevicePlayer', 'Open Device Player')}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ── Web step 1: browser preview ────────────────────────────────────────────
function WebPlayerStep1({ t }: { t: TFunction }) {
  return (
    <div className="max-w-3xl mx-auto pt-8 pb-16 text-center">
      <WizardHero
        title={t('screens.wizard.choosePlayerTitle', 'Choose the player type you want to register on WeTook')}
        subtitle={t('screens.wizard.choosePlayerSubtitle', "Select how you'd like to connect and register your player.")}
      />

      <BrowserMock t={t} className="mx-auto max-w-md" />

      <div className="mt-8 flex flex-col items-center gap-3">
        <Button onClick={openWebPlayer} className="gap-2 rounded-full h-11 px-6">
          <Globe className="w-4 h-4" />
          {t('screens.wizard.openTvWetook', 'Open {{host}}', { host: WEB_PLAYER_HOST })}
        </Button>
        <p className="text-sm text-muted-foreground">
          {t('screens.wizard.webPlayerHint', 'Open {{host}} on the device you want to register, then continue.', { host: WEB_PLAYER_HOST })}
        </p>
      </div>
    </div>
  )
}

// ── Device step 1a: OS picker ─────────────────────────────────────────────
function DeviceOSPicker({
  onPick, t,
}: {
  onPick: (os: DeviceOS) => void
  t: TFunction
}) {
  return (
    <div className="max-w-3xl mx-auto pt-8 pb-16 text-center">
      <WizardHero
        title={t('screens.wizard.choosePlayerTitle', 'Choose the player type you want to register on WeTook')}
        subtitle={t('screens.wizard.choosePlayerSubtitle', "Select how you'd like to connect and register your player.")}
      />

      <div className="grid grid-cols-2 gap-5 max-w-xl mx-auto">
        {OS_LIST.map(os => (
          <button
            key={os.key}
            type="button"
            onClick={() => onPick(os.key)}
            className={cn(
              'aspect-square rounded-3xl bg-card border border-[hsl(var(--border-card,var(--border)))] hover:border-primary/60',
              'flex flex-col items-center justify-center gap-4 transition-all',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            )}
          >
            {/* Official platform wordmarks already carry the name, so the
                cards don't repeat it as a text label below. */}
            {os.key === 'android-tv' ? (
              <AndroidTvWordmark />
            ) : (
              <AndroidWordmark />
            )}
          </button>
        ))}
      </div>
    </div>
  )
}

// ── Device step 1b: install guide for chosen OS ───────────────────────────
function DeviceInstallGuide({
  os, onChangeOS, t,
}: {
  os: DeviceOS
  onChangeOS: () => void
  t: TFunction
}) {
  const heading = ({
    android:      t('screens.wizard.installAndroidTitle',   'Install the WeTook player on Android'),
    'android-tv': t('screens.wizard.installAndroidTvTitle', 'Install the WeTook player on Android TV'),
  } as Record<DeviceOS, string>)[os]

  const steps = ({
    android: [
      t('screens.wizard.installAndroid1', 'Open the Play Store on your Android phone or tablet.'),
      t('screens.wizard.installAndroid2', 'Search for "WeTook Player" and install the app.'),
      t('screens.wizard.installAndroid3', 'Launch the app — a 6-digit pairing code will appear on screen.'),
    ],
    'android-tv': [
      t('screens.wizard.installAndroidTv1', 'Open the Play Store on your Android TV.'),
      t('screens.wizard.installAndroidTv2', 'Search for "WeTook Player" and install the app.'),
      t('screens.wizard.installAndroidTv3', 'Launch the app — a 6-digit pairing code will appear on screen.'),
    ],
  } as Record<DeviceOS, string[]>)[os]

  return (
    <div className="max-w-3xl mx-auto pt-8 pb-16">
      <button
        type="button"
        onClick={onChangeOS}
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-6"
      >
        {/* Always ChevronLeft — a global CSS rule mirrors it under dir="rtl";
            see WizardNavButton.tsx for why swapping here too double-flips it. */}
        <ChevronLeft className="w-3.5 h-3.5" />
        {t('screens.wizard.changeOS', 'Pick a different OS')}
      </button>

      <div className="flex items-center gap-4 mb-6">
        <div className="w-16 h-16 rounded-2xl bg-card flex items-center justify-center shrink-0">
          <OSLogo os={os} size={32} />
        </div>
        <div>
          <h2 className="text-xl font-bold">{heading}</h2>
          <p className="text-xs text-muted-foreground mt-1">
            {t('screens.wizard.installSubtitle', 'Follow these steps on the device you want to register, then continue.')}
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          <ol className="space-y-4">
            {steps.map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-foreground text-background text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <span className="text-sm leading-relaxed">{s}</span>
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>
    </div>
  )
}

// ── Step 2: Enter Screen Code ──────────────────────────────────────────────
function EnterScreenCode({
  code, onChange, codeError, playerType, remainingLicenses, t,
}: {
  code: string
  onChange: (v: string) => void
  codeError: string | null
  playerType: PlayerType
  remainingLicenses: number
  t: TFunction
}) {
  // remainingLicenses is the org's whole seat pool — a real gating fact (the
  // pairing itself draws from that same pool regardless of branch), so the
  // gate stays accurate to it for everyone. The raw org-wide NUMBER is only
  // shown to full-access users; a branch-scoped viewer/branch_manager just
  // sees whether pairing is currently possible, not how many seats the whole
  // org has left.
  const { fullAccess } = usePermissions()
  const hasLicense = remainingLicenses > 0
  const [licenseRequestOpen, setLicenseRequestOpen] = useState(false)

  const intro = playerType === 'web'
    ? t('screens.wizard.codeSubtitleWeb',    'Enter the 6-character code displayed on the {{host}} browser tab to connect it.', { host: WEB_PLAYER_HOST })
    : t('screens.wizard.codeSubtitleDevice', 'Enter the 6-digit code displayed on your device screen to connect it to WeTook.')

  return (
    <div className="max-w-2xl mx-auto pt-8 pb-16">
      {/* License banner */}
      <Card className="mb-10 border-border bg-card">
        <CardContent className="p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-muted/40 border border-border flex items-center justify-center">
              <KeyRound className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="flex flex-col">
              {fullAccess ? (
                <>
                  <span className="text-xl font-bold leading-tight tabular-nums">{remainingLicenses}</span>
                  <span className="text-xs text-muted-foreground">
                    {t('screens.wizard.remainingLicenses', 'Remaining licenses')}
                  </span>
                </>
              ) : (
                <span className="text-sm font-medium leading-tight">
                  {hasLicense
                    ? t('screens.wizard.licensesAvailable', 'Licenses available')
                    : t('screens.wizard.noLicensesAvailable', 'No licenses available')}
                </span>
              )}
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            className="gap-1.5 rounded-full"
            onClick={() => setLicenseRequestOpen(true)}
          >
            <KeyRound className="w-3.5 h-3.5" />
            {t('screens.wizard.buyLicenses', 'Buy Licenses')}
          </Button>
        </CardContent>
      </Card>

      <LicenseRequestDialog
        open={licenseRequestOpen}
        onClose={() => setLicenseRequestOpen(false)}
      />

      <h2 className="text-2xl font-bold text-center mb-2">
        {t('screens.wizard.codeTitle', 'Enter Your Screen Code')}
      </h2>
      <p className="text-sm text-muted-foreground text-center mb-8">{intro}</p>

      <div className="rounded-2xl border border-border bg-muted/20 px-6 py-10 flex flex-col items-center">
        <OtpInput
          value={code}
          onChange={onChange}
          length={6}
          type="alphanumeric"
          separatorAfter={2}
          invalid={!!codeError}
          autoFocus
        />
      </div>

      {codeError && (
        <p className="mt-4 text-sm text-destructive text-center">{codeError}</p>
      )}

      {/* Web flow — reopen the player in a new tab to read the code. */}
      {playerType === 'web' && (
        <div className="mt-6 flex justify-center">
          <Button variant="outline" onClick={openWebPlayer} className="gap-2 rounded-full h-10 px-5">
            <Globe className="w-4 h-4" />
            {t('screens.wizard.openTvWetook', 'Open {{host}}', { host: WEB_PLAYER_HOST })}
          </Button>
        </div>
      )}

      {!hasLicense && (
        <p className="mt-6 text-xs text-center text-muted-foreground">
          {t('screens.wizard.noLicenseHint', 'You need at least one license to register a screen. Purchase a license to continue.')}
        </p>
      )}
    </div>
  )
}

// ── Step 3: Screen Details (placeholder) ───────────────────────────────────
function ScreenDetailsPlaceholder({
  form, setForm, branches, formErrors, setFormErrors, t, autoNameHint,
}: {
  form: typeof EMPTY
  setForm: (updater: (f: typeof EMPTY) => typeof EMPTY) => void
  branches: Branch[]
  formErrors: { branch_id?: string }
  setFormErrors: (next: { branch_id?: string }) => void
  t: TFunction
  autoNameHint?: string
}) {
  return (
    <div className="max-w-3xl mx-auto pt-8 pb-16 space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">
          {t('screens.wizard.detailsTitle', 'Screen Details')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('screens.wizard.detailsSubtitle', "Name the screen and set how it is mounted. A name is suggested for you — edit it here, or rename later from the screen details page.")}
        </p>
      </div>

      <Card>
        <CardContent className="p-6 space-y-5">
          {/* The suggested name is a starting point, not a verdict. It stays
              in sync while it is untouched (see the previewName effect) and
              stops the moment the operator types their own. */}
          <div className="grid gap-1.5">
            <Label>{t('screens.wizard.autoNameLabel', 'Screen name')}</Label>
            <Input
              value={form.name}
              placeholder={autoNameHint}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="bg-muted/10"
            />
            <p className="text-xs text-muted-foreground">
              {t('screens.wizard.nameHint', 'Leave as suggested, or type your own.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="grid gap-1.5 md:col-span-2">
              <Label>{t('screens.branch', 'Assigned Branch')}</Label>
              <Select
                value={form.branch_id}
                onValueChange={v => { setFormErrors({}); setForm(f => ({ ...f, branch_id: v })) }}
              >
                <SelectTrigger className={cn('bg-muted/10', formErrors.branch_id && 'border-destructive')}>
                  <SelectValue placeholder={t('screens.selectBranch', 'Select Branch...')} />
                </SelectTrigger>
                <SelectContent>
                  {branches.map(b => (
                    <SelectItem key={b.id} value={b.id.toString()}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formErrors.branch_id && (
                <p className="text-xs text-destructive">{formErrors.branch_id}</p>
              )}
            </div>

            <div className="grid gap-1.5">
              <Label>{t('screens.orientation', 'Orientation')}</Label>
              <Select
                value={form.orientation_type}
                onValueChange={v => setForm(f => ({ ...f, orientation_type: v as 'horizontal' | 'vertical' }))}
              >
                <SelectTrigger className="bg-muted/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="horizontal">
                    <div className="flex items-center gap-2"><Monitor className="w-4 h-4" /> {t('screens.orientHorizontal', 'Horizontal')}</div>
                  </SelectItem>
                  <SelectItem value="vertical">
                    <div className="flex items-center gap-2"><Smartphone className="w-4 h-4" /> {t('screens.orientVertical', 'Vertical')}</div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Orientation is the aspect the player renders (16:9 vs 9:16);
                rotation is how the panel is physically mounted. Both were
                already saved by this wizard — only rotation had no control,
                so a rotated screen had to be fixed after pairing. */}
            <div className="grid gap-1.5">
              <Label>{t('screens.rotation', 'Rotation')}</Label>
              <Select
                value={String(form.rotation_degrees)}
                onValueChange={v => setForm(f => ({ ...f, rotation_degrees: Number(v) as 0 | 90 | 180 | 270 }))}
              >
                <SelectTrigger className="bg-muted/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[0, 90, 180, 270].map(deg => (
                    <SelectItem key={deg} value={String(deg)}>
                      <div className="flex items-center gap-2">
                        <RotateCw className="w-4 h-4" style={{ transform: `rotate(${deg}deg)` }} />
                        {deg}°
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

        </CardContent>
      </Card>

      <Card className="bg-muted/20 border-dashed">
        <CardContent className="p-4 flex items-center gap-3 text-muted-foreground">
          <MonitorPlay className="w-4 h-4 shrink-0" />
          <p className="text-xs">
            {t('screens.wizard.placeholderNote', 'Additional configuration (advanced sync, scheduled playlists, etc.) will move to this page in a future update.')}
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function ScreenBuilderPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()

  const isNew = !id || id === 'new'

  // Web-player deep link: the TV displays a QR like
  //   https://frame.wetook.net/screens/new?code=ABC123
  // Scanning it lands the operator directly on step 2 with the pairing code
  // prefilled, so they only need to confirm the name + branch on step 3.
  // playerType=web matches the player runtime so the wizard skips the OS
  // install guide that's only relevant for the Android APK path.
  const prefilledCode = (searchParams.get('code') ?? '').toUpperCase()

  // Pre-wizard: branch picker. Wizard begins once playerType is set.
  // Deep-link with ?code= jumps straight to step 2 in web flow.
  const [playerType, setPlayerType] = useState<PlayerType | null>(prefilledCode ? 'web' : null)
  const [selectedOS, setSelectedOS] = useState<DeviceOS | null>(null)

  const [step, setStep] = useState<1 | 2 | 3>(prefilledCode ? 2 : 1)
  const [pairingCode, setPairingCode] = useState<string>(prefilledCode)

  const [initialLoading, setInitialLoading] = useState(true)
  // Opaque public_id of the screen being edited (edit flow only); screen
  // mutations address the API by public_id, not the internal int.
  const [editPublicId, setEditPublicId] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [codeError, setCodeError] = useState<string | null>(null)
  const [formErrors, setFormErrors] = useState<{ branch_id?: string }>({})
  const [branches, setBranches] = useState<Branch[]>([])
  const [form, setForm] = useState({ ...EMPTY })
  // Live seat count from /app-subscriptions/signage. 0 until the fetch
  // resolves so the wizard fails closed (no pairing) on a slow network
  // rather than letting the operator submit before the gate is known.
  const [remainingLicenses, setRemainingLicenses] = useState(0)

  useEffect(() => {
    const load = async () => {
      try {
        const [bRes, subRes] = await Promise.all([
          getBranches(),
          getAppSubscription('signage').catch(() => null),
        ])
        setBranches(bRes.data)
        if (subRes?.data) setRemainingLicenses(subRes.data.seats_remaining)

        if (!isNew && id) {
          const sRes = await getScreens()
          const scr = sRes.data.find((s: any) => s.id.toString() === id || s.public_id === id)
          if (scr) {
            setEditPublicId(scr.public_id)
            setForm({
              name: scr.name, status: scr.status,
              orientation_type: scr.orientation_type === 'vertical' ? 'vertical' : 'horizontal',
              rotation_degrees: ([0, 90, 180, 270].includes(scr.rotation_degrees) ? scr.rotation_degrees : 0) as 0 | 90 | 180 | 270,
              branch_id: scr.branch_id?.toString() ?? '', location: scr.location ?? '',
              ip_address: scr.ip_address ?? '',
            })
          } else {
            navigate('/screens')
          }
        }
      } catch (err) {
        console.error(err)
      } finally {
        setInitialLoading(false)
      }
    }
    load()
  }, [id, isNew, navigate])

  // Refetch the seat count when the tab regains focus so a mid-wizard admin
  // grant takes effect without the operator restarting the flow.
  useEffect(() => {
    const onFocus = () => getAppSubscription('signage')
      .then(r => setRemainingLicenses(r.data.seats_remaining))
      .catch(() => {})
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [])

  const hasLicense = remainingLicenses > 0

  const verifyCodeAndAdvance = async () => {
    setCodeError(null)
    setIsVerifying(true)
    try {
      await validatePairingCode(pairingCode)
      setStep(3)
    } catch (err: any) {
      const detail = err.response?.data?.detail
      setCodeError(detail || t(
        'screens.wizard.codeNotFound',
        'No registered device found with this code. Make sure the player is running and showing this code.',
      ))
    } finally {
      setIsVerifying(false)
    }
  }

  // Auto-naming on new-screen pair. Format: `{Player} {BranchIdx} {Seq}` where
  // BranchIdx is the branch's 1-based position in the org's branch list and
  // Seq is "next number" for that branch across the whole org. Stored as-is in
  // Screen.name; operators can rename later from the details page.
  const playerLabelForName = (pt: PlayerType | null, os: DeviceOS | null): string => {
    if (pt === 'web') return 'Web'
    if (pt !== 'device') return 'Device'
    switch (os) {
      case 'android':    return 'Android'
      case 'android-tv': return 'Android TV'
      default:           return 'Device'
    }
  }

  const computeAutoName = async (): Promise<string> => {
    const bidNum = form.branch_id ? Number(form.branch_id) : undefined
    const branchIdx = bidNum
      ? Math.max(1, branches.findIndex(b => b.id === bidNum) + 1)
      : 0
    let seq = 1
    try {
      const sRes = await getScreens()
      const branchScreens = (sRes.data as any[]).filter(s => s.branch_id === bidNum)
      seq = branchScreens.length + 1
    } catch {
      // best-effort — fall back to seq=1 so we still produce a deterministic name
    }
    const player = playerLabelForName(playerType, selectedOS)
    return `${player} ${branchIdx} ${seq}`
  }

  const [previewName, setPreviewName] = useState('')
  // The last name WE generated. Changing branch recomputes the suggestion, and
  // that must keep refreshing the field while the operator has not touched it —
  // but must never overwrite a name they typed themselves. Comparing against
  // this ref is what separates the two cases; a boolean "touched" flag would
  // also be cleared by our own writes.
  const lastAutoName = useRef('')
  useEffect(() => {
    if (!isNew || step !== 3) return
    let cancelled = false
    computeAutoName().then(n => {
      if (cancelled) return
      setPreviewName(n)
      setForm(f => (
        f.name.trim() === '' || f.name === lastAutoName.current ? { ...f, name: n } : f
      ))
      lastAutoName.current = n
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, step, playerType, selectedOS, form.branch_id, branches])

  const save = async () => {
    // A typed name wins. Previously this recomputed the auto name at submit
    // time for every new screen, so anything the operator entered was silently
    // discarded — the field may as well have been read-only, which it was.
    const typedName = form.name.trim()
    const finalName = typedName
      || (isNew ? await computeAutoName() : `Display-${pairingCode || Date.now()}`)
    const payload = {
      name: finalName,
      status: form.status,
      orientation_type: form.orientation_type,
      rotation_degrees: form.rotation_degrees,
      branch_id: form.branch_id ? Number(form.branch_id) : undefined,
      location: form.location || undefined,
      ip_address: form.ip_address || undefined,
    }

    setIsSaving(true)
    try {
      if (isNew) {
        await pairScreen({ ...payload, pairing_code: pairingCode })
      } else if (editPublicId) {
        await updateScreen(editPublicId, payload)
      }
      navigate('/screens')
    } catch (err: any) {
      console.error(err)
      toast.error(err.response?.data?.detail || t('common.updateFailed', 'Failed to save screen.'))
    } finally {
      setIsSaving(false)
    }
  }

  // Back behavior cascades inside step 1 (device: install-guide → OS picker →
  // pre-wizard); web step 1 → pre-wizard; step 2 → step 1; step 3 → step 2.
  const handleBack = () => {
    setCodeError(null)
    if (step === 1) {
      if (playerType === 'device' && selectedOS) { setSelectedOS(null); return }
      setPlayerType(null)
      return
    }
    if (step === 2) { setStep(1); return }
    if (step === 3) { setStep(2); return }
  }

  const handleNext = () => {
    if (step === 1) {
      // Device flow needs an OS picked before leaving step 1.
      if (playerType === 'device' && !selectedOS) return
      setStep(2)
      return
    }
    if (step === 2) {
      if (!hasLicense || pairingCode.length < 6) return
      verifyCodeAndAdvance()
      return
    }
    save()
  }

  const step1Ready =
    playerType === 'web'
    || (playerType === 'device' && selectedOS !== null)

  const nextDisabled =
    (step === 1 && !step1Ready)
    || (step === 2 && (!hasLicense || pairingCode.length < 6 || isVerifying))
    || (step === 3 && isSaving)

  // Replace the regular TopBar with the wizard's own bar while setting up a
  // new screen — pre-wizard gets a back-only bar; the 3-step flow gets the
  // full stepper. Edit mode falls back to the normal "Screens" title.
  const headerRender = useMemo<ReactNode | undefined>(() => {
    if (!isNew) return undefined
    if (!playerType) {
      return <BackOnlyBar onBack={() => navigate('/screens')} t={t} />
    }
    // Progress only in the TopBar; Back / Next moved to the content footer.
    return <StepperBar step={step} t={t} />
  }, [
    isNew, playerType, step, navigate, t,
  ])

  usePageHeader(
    isNew
      ? { render: headerRender }
      : { title: t('nav.screens', 'Screens') }
  )

  if (initialLoading) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
  }

  // ── Edit mode (existing screen) — single details panel, classic save. ────
  if (!isNew) {
    return (
      <div className="max-w-5xl mx-auto space-y-6 pb-20 px-6 pt-6">
        <div className="flex items-center justify-between border-b pb-4">
          <h2 className="text-2xl font-bold">{t('screens.editScreen', 'Edit Screen')}</h2>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate('/screens')}>{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={save} disabled={isSaving} className="gap-2">
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {t('common.save', 'Save')}
            </Button>
          </div>
        </div>
        <ScreenDetailsPlaceholder
          form={form}
          setForm={setForm}
          branches={branches}
          formErrors={formErrors}
          setFormErrors={setFormErrors}
          t={t}
        />
      </div>
    )
  }

  // ── Pre-wizard branch picker (back bar lives in the TopBar render) ──────
  if (!playerType) {
    return (
      <ChooseBranch
        onPick={(p) => { setPlayerType(p); setStep(1); setSelectedOS(null) }}
        t={t}
      />
    )
  }

  // ── Wizard ──────────────────────────────────────────────────────────────
  // Back / Next flank the content on the left and right (vertically centred),
  // not stacked in a footer below it. The middle column scrolls on its own.
  const nextLoading = (step === 2 && isVerifying) || (step === 3 && isSaving)
  return (
    <div className="flex items-stretch gap-1 sm:gap-3 flex-1 min-h-0">
      {/* Back — start edge, vertically centred */}
      <div className="flex items-center shrink-0">
        <WizardNavButton
          direction="back"
          variant="secondary"
          onClick={handleBack}
          label={t('common.back', 'Back')}
          hideLabelOnMobile
          className="rounded-full h-10 px-3 sm:px-4"
        />
      </div>

      {/* Content — scrolls independently */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {step === 1 && playerType === 'web' && <WebPlayerStep1 t={t} />}
        {step === 1 && playerType === 'device' && !selectedOS && (
          <DeviceOSPicker onPick={setSelectedOS} t={t} />
        )}
        {step === 1 && playerType === 'device' && selectedOS && (
          <DeviceInstallGuide
            os={selectedOS}
            onChangeOS={() => setSelectedOS(null)}
            t={t}
          />
        )}
        {step === 2 && (
          <EnterScreenCode
            code={pairingCode}
            onChange={v => { setCodeError(null); setPairingCode(v) }}
            codeError={codeError}
            playerType={playerType}
            remainingLicenses={remainingLicenses}
            t={t}
          />
        )}
        {step === 3 && (
          <ScreenDetailsPlaceholder
            form={form}
            setForm={setForm}
            branches={branches}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
            autoNameHint={previewName}
            t={t}
          />
        )}
      </div>

      {/* Next / Finish — end edge, vertically centred */}
      <div className="flex items-center shrink-0">
        <WizardNavButton
          direction="forward"
          onClick={handleNext}
          disabled={nextDisabled}
          loading={nextLoading}
          label={step === 3 ? t('common.finish', 'Finish') : t('common.next', 'Next')}
          hideChevron={step === 3}
          hideLabelOnMobile
          className="gap-2 rounded-full h-10 px-3 sm:px-5 font-semibold shadow-sm shadow-primary/25 hover:shadow-md hover:shadow-primary/30 transition-shadow"
        />
      </div>
    </div>
  )
}
